import { z } from 'zod'

/**
 * for maximum flexibility, capabilities are stored as strings in the database, but they should
 * be kept in sync with this enum schema. if the `/me` endpoint returns a capability that is not
 * in this enum, it is logged to the console as a warning.
 */
export const UserCapabilitySchema = z.enum([
  'account_biller_assignment.manage',
  'account.members.manage.biller',
  'account.members.manage.billing_manager',
  'account.members.manage.billing_supervisor',
  'account.members.manage.joyful_admin',
  'account.members.manage.practice_admin',
  'account.members.manage.practice_member',
  'account.members.manage.practice_viewer',
  'account.members.manage.rcm_specialist',
  'account.members.manage.services_practice_admin',
  'account.members.view.biller',
  'account.members.view.billing_manager',
  'account.members.view.billing_supervisor',
  'account.members.view.joyful_admin',
  'account.members.view.practice_admin',
  'account.members.view.practice_member',
  'account.members.view.practice_viewer',
  'account.members.view.rcm_specialist',
  'account.members.view.services_practice_admin',
  'account.members.view',
  'admin.capabilities.manage',
  'agent_mail.send',
  'agent_mail.view',
  'announcement.manage',
  'announcement.view.all',
  'app.app.view',
  'app.app.view.full',
  'app.bpo.view',
  'app.internal.view',
  'app.workspace.view',
  'admin.agent_phone_calls.view',
  'admin.bridge_inbox.manage',
  'admin.capabilities.view',
  'admin.contracts.manage',
  'admin.data_oncall.manage',
  'admin.debug',
  'deployment.view',
  'admin.feature_flags.manage',
  'admin.hotkeys',
  'admin.ingestion_health.manage',
  'admin.invoices.view',
  'admin.kpis.view',
  'admin.mcp.query_sql',
  'agent',
  'agent.chat.respond_to_old',
  'agent.memories.manage',
  'agent.memories.view',
  'agent.prompts.view',
  'agent.proposals.edit',
  'agent.proposals.review',
  'agent.proposals.view',
  'agent.skills.manage',
  'agent.skills.manage_all',
  'agent.skills.view',
  'agent.system_prompt.manage',
  'analysis.edit',
  'analysis.library.view',
  'analysis.query.view',
  'analysis.tags.manage',
  'analysis.view',
  'analysis.view.unscoped',
  'analytics.metrics.view',
  'analytics.semantic_gap.review',
  'commenting.configure.digest',
  'commenting.delete.bot',
  'commenting.edit.bot',
  'credentials.invalidate',
  'credentials.list',
  'credentials.manage',
  'credentials.queue.skip',
  'dashboard.audit.view',
  'dashboard.chart.recovery.view',
  'dashboard.config.manage',
  'dashboard.denials_intelligence.view',
  'dashboard.invoice.view',
  'dashboard.internal.view',
  'dashboard.internal.view.landing',
  'dashboard.metabase.embed.view',
  'encounter_audit.review',
  'encounter.action_perform.without_assignment',
  'encounter.action_perform',
  'encounter.action_result.delete',
  'encounter.action_result.edit',
  'encounter.action_result.view',
  'encounter.action.close',
  'encounter.action.confirm_payment',
  'encounter.action.draft_appeal',
  'encounter.action.escalate.joyful',
  'encounter.action.escalate.practice',
  'encounter.action.escalate.supervisor',
  'encounter.action.mark_as_error',
  'encounter.action.record.appeal',
  'encounter.action.record.claim_resubmission',
  'encounter.action.record.denial',
  'encounter.action.record.documentation',
  'encounter.action.record.payment',
  'encounter.action.record.recoupment',
  'encounter.action.record.rejection',
  'encounter.action.record.research',
  'encounter.action.resolve',
  'encounter.action.snooze.acknowledge',
  'encounter.action.snooze.long_duration',
  'encounter.action.snooze.override_stale_due',
  'encounter.action.snooze.rejection',
  'encounter.action.snooze',
  'encounter.action.urgency.view',
  'encounter.aging.view',
  'encounter.amounts.detailed.view',
  'encounter.amounts.recovered.view',
  'encounter.assignment.biller.assign.self',
  'encounter.assignment.biller.assign',
  'encounter.assignment.biller.view',
  'encounter.assignment.user.assign',
  'encounter.assignment.user.view',
  'encounter.biller_agent.interrupt',
  'encounter.biller_agent.run',
  'encounter.biller_agent.view',
  'encounter.billing_type.view',
  'encounter.case.view',
  'encounter.claim_status_check.run',
  'encounter.claim_status_check.view',
  'encounter.close_date.view',
  'encounter.commenting.delete',
  'encounter.commenting.edit',
  'encounter.commenting.view.external',
  'encounter.commenting.view.internal',
  'encounter.commenting.view.rewritten',
  'encounter.commenting.write.external',
  'encounter.commenting.write.internal',
  'encounter.codes.view',
  'encounter.details.view',
  'encounter.details.claim_billing.view',
  'encounter.details.insurance.view',
  'encounter.details.off_platform_responses.view',
  'encounter.details.payer_response.view',
  'encounter.due_date.view',
  'encounter.eligibility_check.run',
  'encounter.eligibility_check.view',
  'encounter.enrichment_status.view',
  'encounter.enrichment_status_override.manage.non_assigned',
  'encounter.enrichment_status_override.manage.non_flagged_for_joyful_followup',
  'encounter.enrichment_status_override.manage.write_off',
  'encounter.enrichment_status_override.manage',
  'encounter.enrichment_status_override.view',
  'encounter.export',
  'encounter.flag_for_joyful_followup.flag',
  'encounter.flag_for_joyful_followup.unflag',
  'encounter.flag_for_joyful_followup.view',
  'encounter.last_biller_action.view',
  'encounter.list.view',
  'encounter.merge',
  'encounter.sidebar.default_open',
  'encounter.summary.history.view',
  'encounter.tags.manage',
  'encounter.tags.view',
  'encounter.timeline.view.biller_activity',
  'encounter.timeline.view.joyful_activity',
  'encounter.uncollectible.sign_off',
  'encounter.work_blocks.view',
  'encounter.work_blocks.view_all_users',
  'encounter.work_blocks.export',
  'encounter.work_blocks.generate',
  'encounter.work_blocks.regenerate',
  'encounter.work_blocks.manage',
  'encounter.work_blocks.manage.completion',
  'encounter.work_blocks.manage.due_date_rules',
  'encounter.work_blocks.reserve.view',
  'encounter.work_blocks.reserve.self_assign',
  'encounter.workflow_status.manage.closed',
  'encounter.workflow_status.manage.complete',
  'encounter.workflow_status.manage.return_to_biller',
  'encounter.workflow_status.view.complete',
  'encounter.workflow_status.view',
  'extension.sidecar.view',
  'fax.link',
  'fax.view',
  'faxes.send',
  'kpi_email.configure',
  'kpi_email.manage',
  'mail.send',
  'kpi_email.send',
  'kpi_email.view',
  'logic_diagram.manage',
  'logic_diagram.view',
  'outbound_packets.draft',
  'outbound_packets.record_portal_upload',
  'outbound_packets.review',
  'outbound_packets.submit',
  'outbound_packets.view',
  'phone.call.place.unflagged_encounter',
  'phone.call.place',
  'phone.monitor',
  'phone.provision_number',
  'phone.view.all',
  'phone.view',
  'automation.manage',
  'automation.view',
  'practice_address.view',
  'practice_address.manage',
  'project.commenting.delete',
  'project.commenting.edit',
  'project.commenting.view.external',
  'project.commenting.view.internal',
  'project.commenting.view.rewritten',
  'project.commenting.write.external',
  'project.commenting.write.internal',
  'project.encounters.view',
  'project.manage.any',
  'project.manage',
  'project.priority.view',
  'project.priority.manage',
  'project.membership.manage',
  'project.view.admin',
  'project.view.internal',
  'project.view.practice',
  'project.visibility.manage',
  'project.visibility.view',
  'dropbox.link',
  'dropbox.linked_record.view',
  'dropbox.upload',
  'dropbox.view',
  'phone_call_task.close',
  'phone_call_task.comment',
  'phone_call_task.manage',
  'phone_call_task.view',
  'claim_scrubber.release',
  'claim_scrubber.rules.manage',
  'claim_scrubber.rules.publish',
  'claim_scrubber.view',
  'claim_scrubber.work',
  'practice_request.agent.view',
  'practice_request.commenting.delete',
  'practice_request.commenting.edit',
  'practice_request.commenting.view.external',
  'practice_request.commenting.view.internal',
  'practice_request.commenting.write',
  'practice_request.commenting.write.external',
  'practice_request.commenting.write.internal',
  'practice_request.configure',
  'practice_request.configure.subscribers',
  'practice_request.create',
  'practice_request.delete',
  'practice_request.encounters.view',
  'practice_request.manage.any',
  'practice_request.manage',
  'practice_request.publish',
  'practice_request.publish.approve',
  'practice_request.reopen',
  'practice_request.revert_to_draft',
  'practice_request.snooze',
  'practice_request.view.admin',
  'practice_request.view.internal',
  'practice_request.view.practice',
  'practice_request.view.subscribers',
  'practice_request.visibility.manage',
  'practice_request.visibility.view',
  'product_change.propose',
  'uncollectible.policy.configure',
  'uncollectible_outcome.recovery_step.rewrite',
  'user.biller_details.view',
  'user.view.all',
  'windows_desktop.control',
  'windows_desktop.view',
])

export type UserCapability = z.infer<typeof UserCapabilitySchema>

interface HasCapabilityOptions {
  shouldCheckStrictCapabilities?: boolean
}

export type HasCapability = (
  capability: UserCapability,
  options?: HasCapabilityOptions,
) => boolean

interface CapabilityGatedDefinition {
  capability?: UserCapability
}

export function filterDefinitionsByCapability<
  TDefinition extends CapabilityGatedDefinition,
>(
  definitions: ReadonlyArray<TDefinition>,
  hasCapability: HasCapability,
): Array<TDefinition> {
  return definitions.filter((definition) => {
    return !definition.capability || hasCapability(definition.capability)
  })
}
